import {
  Children,
  Fragment,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Platform, View, type ViewProps } from 'react-native';
import { type SpacingScale, spacing as spacingTokens } from '../../theme/spacing';
import { Box } from './Box';

interface StackProps extends Omit<ViewProps, 'children'> {
  children?: ReactNode;
  spacing?: SpacingScale;
  direction?: 'vertical' | 'horizontal';
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  wrap?: boolean;
  reverse?: boolean;
  fill?: boolean;
  divider?: ReactElement;
}

const alignmentMap = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
} as const;

const justifyMap = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
} as const;

export const Stack = forwardRef<View, StackProps>(
  (
    {
      children,
      spacing = 'md',
      direction = 'vertical',
      align = 'stretch',
      justify = 'start',
      wrap = false,
      reverse = false,
      fill = false,
      divider,
      style,
      ...rest
    },
    ref,
  ) => {
    const isHorizontal = direction === 'horizontal';

    const flexDirection = (() => {
      if (isHorizontal && reverse) return 'row-reverse';
      if (isHorizontal && !reverse) return 'row';
      if (!isHorizontal && reverse) return 'column-reverse';
      return 'column';
    })();

    const childrenArray = Children.toArray(children).filter(
      (child) => isValidElement(child) && child !== null,
    );

    // RN iOS / Yoga drops `gap` unreliably when HStack mixes intrinsic-sized
    // children (icons) with flex:1 children (VStacks). Result: icons overlap
    // labels and flex:1 children collapse. Web's CSS `gap` works fine. So
    // on native we render explicit Spacer Views between children; on web we
    // keep using `gap`. Spacers are robust regardless of whether children
    // forward `style`.
    const useSpacers = Platform.OS !== 'web';
    const gapValue = spacingTokens[spacing];
    const spacerStyle = isHorizontal
      ? { width: gapValue, flexShrink: 0 }
      : { height: gapValue, flexShrink: 0 };

    const renderChildren = () => {
      const nodes: ReactNode[] = [];

      childrenArray.forEach((child, index) => {
        const childKey = isValidElement(child)
          ? (child.key ?? `stack-item-${index}`)
          : `stack-item-${index}`;

        if (index > 0) {
          if (divider) {
            nodes.push(<Fragment key={`stack-divider-${index}`}>{divider}</Fragment>);
          } else if (useSpacers) {
            nodes.push(<View key={`stack-spacer-${index}`} style={spacerStyle} />);
          }
        }

        nodes.push(<Fragment key={childKey}>{child}</Fragment>);
      });

      return nodes;
    };

    return (
      <Box
        ref={ref}
        flexDirection={flexDirection as any}
        alignItems={alignmentMap[align] as any}
        justifyContent={justifyMap[justify] as any}
        flexWrap={wrap ? 'wrap' : 'nowrap'}
        flex={fill ? 1 : undefined}
        gap={useSpacers ? undefined : spacing}
        style={style}
        {...rest}
      >
        {renderChildren()}
      </Box>
    );
  },
);

Stack.displayName = 'Stack';

interface HStackProps extends Omit<StackProps, 'direction'> {}

export const HStack = forwardRef<View, HStackProps>((props, ref) => {
  return <Stack ref={ref} direction="horizontal" {...props} />;
});

HStack.displayName = 'HStack';

interface VStackProps extends Omit<StackProps, 'direction'> {}

export const VStack = forwardRef<View, VStackProps>((props, ref) => {
  return <Stack ref={ref} direction="vertical" {...props} />;
});

VStack.displayName = 'VStack';
